import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { 
  GET_ALL_CATEGORIES,
  GET_ALL_SUB_CATEGORIES, 
  CREATE_SUB_CATEGORY, 
  UPDATE_SUB_CATEGORY, 
  DELETE_SUB_CATEGORY
} from '../graphql/subCategoryOperations';
import { 
  TrashIcon, 
  PencilIcon, 
  PlusIcon
} from '@heroicons/react/24/solid';
import { handleGraphQLError, formatDate } from '../utils/errorHandler';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import ManagementModal from '../components/ManagementModal';

const SubCategoriesManagement = () => {
  const { isDarkMode } = useTheme();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subCategoryData, setSubCategoryData] = useState({
    name: '',
    categoryId: ''
  });
  const [editingSubCategory, setEditingSubCategory] = useState(null);

  // Fetch categories
  const { 
    data: categoriesData 
  } = useQuery(GET_ALL_CATEGORIES, {
    onError: (error) => handleGraphQLError(error, 'Failed to load categories')
  });

  // Fetch sub-categories
  const { 
    loading: subCategoriesLoading, 
    error: subCategoriesError, 
    data: subCategoriesData, 
    refetch 
  } = useQuery(GET_ALL_SUB_CATEGORIES, {
    onError: (error) => handleGraphQLError(error, 'Failed to load sub-categories')
  });

  // Mutations
  const [createSubCategory] = useMutation(CREATE_SUB_CATEGORY, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Sub-category created successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to create sub-category')
  });

  const [updateSubCategory] = useMutation(UPDATE_SUB_CATEGORY, {
    onCompleted: () => {
      resetForm();
      refetch();
      toast.success('Sub-category updated successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to update sub-category')
  });

  const [deleteSubCategory] = useMutation(DELETE_SUB_CATEGORY, {
    onCompleted: () => {
      refetch();
      toast.success('Sub-category deleted successfully');
    },
    onError: (error) => handleGraphQLError(error, 'Failed to delete sub-category')
  });

  const resetForm = () => {
    setSubCategoryData({
      name: '',
      categoryId: ''
    });
    setEditingSubCategory(null);
    setIsModalOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!subCategoryData.name.trim()) {
      toast.error('Sub-Category name is required');
      return;
    }
    if (!subCategoryData.categoryId) {
      toast.error('Category is required');
      return;
    }

    const input = {
      name: subCategoryData.name.trim(),
      categoryId: subCategoryData.categoryId
    };

    if (editingSubCategory) {
      updateSubCategory({
        variables: {
          updateSubCategoryId: editingSubCategory.id,
          input
        }
      });
    } else {
      createSubCategory({
        variables: { input }
      });
    }
  };

  const handleEdit = (subCategory) => {
    setEditingSubCategory(subCategory);
    setSubCategoryData({
      name: subCategory.name,
      categoryId: subCategory.categoryId
    });
    setIsModalOpen(true);
  };

  const handleDelete = (subCategoryId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this sub-category?');
    if (confirmDelete) {
      deleteSubCategory({
        variables: { deleteSubCategoryId: subCategoryId }
      });
    }
  };

  // Loading and error states
  if (subCategoriesLoading) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      Loading sub-categories...
    </div>
  );

  if (subCategoriesError) return (
    <div className={`text-center py-8 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
      Error loading sub-categories
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Add Sub-Category Button */}
      <div className="flex justify-end mb-4">
        <button 
          onClick={() => {
            setEditingSubCategory(null);
            setIsModalOpen(true);
          }}
          className={`
            flex items-center space-x-2 px-4 py-2 rounded-lg
            ${isDarkMode 
              ? 'bg-blue-600 text-white hover:bg-blue-500' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
            }
            transition
          `}
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Sub-Category</span>
        </button>
      </div>

      {/* Sub-Categories Management Modal */}
      <ManagementModal
        isOpen={isModalOpen}
        onClose={() => {
          resetForm();
          setIsModalOpen(false);
        }}
        onSubmit={handleSubmit}
        title={editingSubCategory ? 'Edit Sub-Category' : 'Create New Sub-Category'}
      >
        {/* Sub-Category Name Input */}
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Sub-Category Name
          </label>
          <input
            type="text"
            value={subCategoryData.name}
            onChange={(e) => setSubCategoryData(prev => ({ ...prev, name: e.target.value }))}
            className={`w-full px-3 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-white text-gray-900 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
            placeholder="Enter sub-category name"
          />
        </div>
        <div className="mb-4">
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Category
          </label>
          <select
            value={subCategoryData.categoryId}
            onChange={(e) => setSubCategoryData(prev => ({ ...prev, categoryId: e.target.value }))}
            className={`w-full px-3 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-white text-gray-900 border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors`}
          >
            <option value="">Select a category</option>
            {categoriesData?.getAllCategories?.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </ManagementModal>

      {/* Sub-Categories List */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        shadow-lg rounded-xl border 
        transition-colors duration-300
      `}>
        {/* Card Header */}
        <div className={`
          px-6 py-4 
          ${isDarkMode ? 'border-b-gray-700' : 'border-b-gray-200'}
          border-b 
          flex justify-between items-center
        `}>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
            Sub-Categories List
          </h2>
          <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Total: {subCategoriesData?.getAllSubCategories?.length || 0} sub-categories
          </div>
        </div>

        {/* Sub-Categories Table */}
        <div className="overflow-x-auto">
          {subCategoriesData.getAllSubCategories.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No sub-categories found
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className={`
                ${isDarkMode 
                  ? 'bg-gray-700 text-gray-300' 
                  : 'bg-gray-50 text-gray-600'
                } 
                border-b border-gray-200 dark:border-gray-600
              `}>
                <tr>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Category</th>
                  <th className="px-6 py-3 text-left">Created At</th>
                  <th className="px-6 py-3 text-left">Updated At</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subCategoriesData.getAllSubCategories.map((subCategory) => (
                  <tr 
                    key={subCategory.id} 
                    className={`
                      border-b 
                      ${isDarkMode 
                        ? 'border-gray-700 hover:bg-gray-700' 
                        : 'border-gray-200 hover:bg-gray-50'
                      }
                      transition duration-150
                    `}
                  >
                    <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {subCategory.name}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                      {categoriesData?.getAllCategories?.find(cat => cat.id === subCategory.categoryId)?.name || 'N/A'}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(subCategory.createdAt)}
                    </td>
                    <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatDate(subCategory.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button 
                          onClick={() => handleEdit(subCategory)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-700' 
                              : 'text-blue-500 hover:text-blue-700 hover:bg-blue-100'
                            }
                            transition
                          `}
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(subCategory.id)}
                          className={`
                            p-2 rounded-full 
                            ${isDarkMode 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-700' 
                              : 'text-red-500 hover:text-red-700 hover:bg-red-100'
                            }
                            transition
                          `}
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubCategoriesManagement;
